'use client';

import { APP_VERSION } from '@payments-checkout/shared/helpers/constants/appVersion';
import { Analitica, type RichEvent } from '@stone-payments/analitica';
import { AmplitudePlugin } from '@stone-payments/analitica/plugin-amplitude';
import { Experiment } from '@stone-payments/experiment';
import { AmplitudeExperimentJS } from '@stone-payments/experiment/plugins/amplitude-experiment-js';
import { env } from 'next-runtime-env';
import { CheckoutDatadogPlugin, beforeSendRumEvent } from './datadog.plugin';

class AmplitudeCheckout extends AmplitudePlugin {
  name = 'AMPLITUDE_CHECKOUT_V3';

  sendEvent(event: RichEvent): void {
    const { metadata, label, ...rest } = event;
    this.instance?.logEvent(label, { ...metadata, ...rest });
  }
}

const environment = env('NEXT_PUBLIC_ENV');
const EXPERIMENT_KEY = env('NEXT_PUBLIC_EXPERIMENT_KEY') ?? '';

export type ExperimentResponseType = 'pending' | 'control' | 'treatment';

export type GlobalWalletVariant =
  | 'pending'
  | 'switch_on_a'
  | 'switch_off_a'
  | 'switch_on_b'
  | 'switch_off_b';

export type GlobalWalletVariantPayload = {
  title: string;
  description: string;
  tooltip_text: string;
};

function numberFromEnv(key: string, fallback: number) {
  const raw = env(key);
  // `Number('')` é 0, não NaN: env vazia zeraria a amostragem em silêncio.
  if (raw === undefined || raw === '') return fallback;

  const parsed = Number(raw);
  return Number.isFinite(parsed) ? parsed : fallback;
}

const isEnabled = (key: string) => env(key) === 'true';

/**
 * Amostragem por env, não por constante: mudar tem impacto de custo, e assim é
 * ajuste no chart sem deploy. Defaults reproduzem o comportamento anterior.
 */
const SESSION_SAMPLE_RATE = numberFromEnv(
  'NEXT_PUBLIC_DATADOG_SESSION_SAMPLE_RATE',
  20
);
const REPLAY_SAMPLE_RATE = numberFromEnv(
  'NEXT_PUBLIC_DATADOG_REPLAY_SAMPLE_RATE',
  5
);
/**
 * Ligado por padrão: com default OFF, um ambiente que esquecesse a env perderia
 * silenciosamente os erros de sessão não amostrada.
 */
const LOGS_ENABLED = env('NEXT_PUBLIC_DATADOG_LOGS_ENABLED') !== 'false';

/**
 * `dryRun` imprime o evento no console em vez de enviar. Desligado em todos os
 * ambientes; `true` volta o comportamento anterior de development. Sem token de
 * RUM localmente, com `false` o evento é descartado pelo SDK.
 */
const DRY_RUN = env('NEXT_PUBLIC_ANALITICA_DRY_RUN') === 'true';

/**
 * Correlação com o APM do backend. Injeta 5 headers `x-datadog-*`/`traceparent`
 * (só IDs de trace) nas URLs que casam. Se o BFF não tiver `dd-trace`, os headers
 * são ignorados — não quebra nada, só não gera link.
 *
 * Lembrando que o link só existe em sessão amostrada: o SDK não injeta header
 * quando a sessão não é tracked.
 */
/**
 * Sem barra final: a env é declarada com barra em sdx/prd e sem em stg, e o match
 * é por prefixo.
 */
const PAGARME_API = (env('NEXT_PUBLIC_PAGARME_API') ?? '').replace(/\/+$/, '');

const TRACING_CONFIG =
  isEnabled('NEXT_PUBLIC_DATADOG_TRACING_ENABLED') && PAGARME_API
    ? {
        allowedTracingUrls: [
          {
            // A barra no sufixo não é cosmética: sem ela o prefixo casaria com
            // `checkout-bff-stg.pagar.me.evil.com`.
            match: (url: string) => url.startsWith(`${PAGARME_API}/`),
            propagatorTypes: ['datadog' as const, 'tracecontext' as const],
          },
        ],
        traceSampleRate: numberFromEnv(
          'NEXT_PUBLIC_DATADOG_TRACE_SAMPLE_RATE',
          100
        ),
      }
    : {};

export const experimentInstance = new Experiment(
  [
    new AmplitudeExperimentJS(EXPERIMENT_KEY, {
      debug: environment === 'development',
      useWithAmplitudeAnalytics: true,
      automaticExposureTracking: true,
    }),
  ],
  {}
);

let experimentInitialized = false;

export async function startExperiment(ctx?: Record<string, unknown>) {
  const isEligible = ctx?.isEligible ?? true;
  if (!isEligible || EXPERIMENT_KEY === '') return;

  if (!ctx?.userId) {
    console.warn('[Experiment] userId não definido.');
    return;
  }

  if (!experimentInitialized) {
    try {
      await experimentInstance.init();
      experimentInitialized = true;
    } catch {
      console.warn(
        '[Experimentos] Não foi possível carregar variações agora. Seguiremos com a experiência padrão.'
      );
      return;
    }
  }

  const user = {
    user_id: ctx?.userId,
    user_properties: {
      account_id: ctx?.accountId,
      merchant_id: ctx?.merchantId,
    },
  };

  return experimentInstance.start(user as Record<string, unknown>);
}

export const analiticaInstance = new Analitica(
  {
    app: {
      env: environment ?? 'production',
      version: APP_VERSION,
    },
    owner: 'link_e_checkout',
    product: 'checkout',
  },
  [
    new CheckoutDatadogPlugin({
      applicationId: env('NEXT_PUBLIC_DATADOG_RUM_ID_PRIVATE_TOKEN') ?? '',
      clientToken: env('NEXT_PUBLIC_DATADOG_RUM_CLIENT_PUBLIC_TOKEN') ?? '',
      site: 'us3.datadoghq.com',
      service: 'pxp-checkout-v3',
      env: environment ?? 'production',
      version: APP_VERSION,
      sessionSampleRate: SESSION_SAMPLE_RATE,
      sessionReplaySampleRate: REPLAY_SAMPLE_RATE,
      trackResources: true,
      trackLongTasks: true,
      trackUserInteractions: true,
      defaultPrivacyLevel: 'mask-user-input',
      beforeSend: beforeSendRumEvent,
      logsEnabled: LOGS_ENABLED,
      ...TRACING_CONFIG,
      costCenter: '870302267',
      project: 'pxp-checkout-v3',
      owner: 'vendas-core-link-e-checkout',
      support: 'vendas-core-link-e-checkout',
      githubOrg: '@stone-payments',
      githubRepo: 'pxp-checkout-v3',
      groupSlack: 'vendas-core-link-e-checkout',
    }),
    new AmplitudeCheckout(env('NEXT_PUBLIC_AMPLITUDE_KEY') ?? '', {
      defaultTracking: false,
    }),
  ],
  { dryRun: DRY_RUN }
);
